import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api, setAuthToken, loadStoredToken } from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const logoutRef = useRef(() => {});
  const loggingOutRef = useRef(false);

  const logout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;

    api.logout().catch(() => {}).finally(() => {
      loggingOutRef.current = false;
    });
    setAuthToken(null);
    localStorage.removeItem("pl_username");
    localStorage.removeItem("pl_role");
    setUser(null);
  }, []);

  logoutRef.current = logout;

  useEffect(() => {
    const token = loadStoredToken();
    const savedUsername = localStorage.getItem("pl_username");
    const savedRole = localStorage.getItem("pl_role");
    if (token && savedUsername) setUser({ username: savedUsername, role: savedRole || "admin" });
    setReady(true);

    const onUnauthorized = () => logoutRef.current();
    window.addEventListener("pl:unauthorized", onUnauthorized);
    return () => window.removeEventListener("pl:unauthorized", onUnauthorized);
  }, []);

  const login = useCallback(async (username, password) => {
    const data = await api.login(username, password);
    setAuthToken(data.token);
    localStorage.setItem("pl_username", data.username);
    localStorage.setItem("pl_role", data.role);
    setUser({ username: data.username, role: data.role });
    return data;
  }, []);

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}